import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fancallWhen } from "@/lib/api";
import { EmptyState, StatusBadge } from "@/components/Bits";

const DOUBLE = ["Jongho", "Yeosang"];

const EntryRow = ({ e, isUnit, onToggle }) => (
  <div data-testid={`raffle-entry-${e.joiner_id}`} className={`flex items-center gap-3 rounded-xl border px-3 sm:px-4 py-3 transition-colors duration-200 ${e.won ? "border-amber-500/50 bg-amber-500/[0.07]" : "border-white/5 bg-zinc-900/60"}`}>
    <Checkbox data-testid={`raffle-won-${e.joiner_id}`} checked={e.won} onCheckedChange={(c) => onToggle(e, !!c)} aria-label={`Mark ${e.joiner_name} as raffle winner`} className="h-5 w-5 border-amber-400 data-[state=checked]:bg-amber-400 data-[state=checked]:text-zinc-950" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold truncate">{e.joiner_name}</span>
        {e.won && <Trophy className="h-4 w-4 text-amber-300 shrink-0" />}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {e.members.length === 0 && <span className="text-xs text-zinc-500">No placed claims</span>}
        {e.members.map((m) => (
          <span key={m} className="rounded-md bg-zinc-800/80 px-1.5 py-0.5 text-[11px] text-zinc-300">{m}{!isUnit && DOUBLE.includes(m) && <span className="text-amber-300 font-mono"> ×2</span>}</span>
        ))}
      </div>
    </div>
    <span data-testid={`raffle-entry-count-${e.joiner_id}`} className="font-mono text-sm font-bold whitespace-nowrap">{e.entries}<span className="text-zinc-500 font-normal">/{isUnit ? 4 : 10}</span></span>
  </div>
);

export default function RaffleDetail({ raffleId, onChanged }) {
  const [r, setR] = useState(null);
  const load = useCallback(() => {
    api.get(`/raffles/${raffleId}`).then((res) => setR(res.data)).catch(() => {});
  }, [raffleId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (e, won) => {
    try {
      await api.put(`/raffles/${raffleId}/winners/${e.joiner_id}`, { won });
      toast.success(won ? `${e.joiner_name} marked as raffle winner` : `${e.joiner_name} unmarked`);
      load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!r) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="min-w-0 rounded-2xl border border-white/10 bg-zinc-900/50 p-5 sm:p-6" data-testid="raffle-detail">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 data-testid="raffle-detail-name" className="font-display text-2xl font-bold tracking-tight">{r.name}</h3>
          <div className="text-sm text-zinc-400 mt-1">{fancallWhen(r) || "Fancall date & time to be announced"}{r.is_unit ? " · Unit Digi: 1 entry per claim, max 4" : ""}</div>
        </div>
        <StatusBadge status={r.status} />
      </div>
      <p className="text-xs text-zinc-500 mt-4">{r.is_unit ? "Opted-in joiners earn 1 entry for each claim placed in a set, up to 4." : "Opted-in joiners earn 1 entry for each member placed in a set (Jongho and Yeosang earn 2), up to 10."} Tick the box next to whoever won the raffle spin.</p>
      <div className="flex justify-between gap-2 text-xs uppercase tracking-widest text-zinc-500 font-semibold mt-6 mb-2 px-1">
        <span>Won · Joiner</span><span data-testid="raffle-total-entries" className="text-right">{r.total_entries} entries</span>
      </div>
      <div className="space-y-2">
        {r.entries.length === 0 && <EmptyState title="No entries yet" hint="Joiners appear here once they opt into the fancall and have a claim placed in a set." />}
        {r.entries.map((e) => <EntryRow key={e.joiner_id} e={e} isUnit={r.is_unit} onToggle={toggle} />)}
      </div>
    </div>
  );
}
